import create from 'zustand';
import {
    formatError,
    login,
    runLogoutTimer,
    saveTokenInLocalStorage,
    signUp,
    getUserProfile,
} from '../services/AuthService';
import { devtools } from 'zustand/middleware';

const initialAuthState = {
    access_token: '',
    expireDate: null,
    casUsername: null,
    casAttributes: null,
    user: {
        id: null,
        email: null,
        nombre: null,
        apellido: null,
        fechaRegistro: null,
        tipoPerfil: null,
        perfilUsuarioId: null,
        perfilEmpresaId: null,
        createdAt: null,
        updatedAt: null,
        perfilUsuario: {
            id: null,
            userId: null,
            telefono: null,
            direccion: null,
            tituloProfesional: null,
            fechaNacimiento: null,
            salarioActual: null,
            salarioDeseado: null,
            idiomas: null,
            genero: null,
            telefonoContacto: null,
            paisResidenciaId: null,
            ciudadResidenciaId: null,
            linkedin: null,
            facebook: null,
            instagram: null,
            github: null,
            x: null,
            createdAt: null,
            updatedAt: null,
            paisResidencia: null,
            ciudadResidencia: null
        },
        perfilEmpresa: {
            id: null,
            userId: null,
            razonSocial: null,
            rnc: null,
            telefono: null,
            sitioWeb: null,
            fechaFundacion: null,
            direccion: null,
            paisId: null,
            ciudadId: null,
            descripcion: null,
            fotoPerfil: null,
            facebook: null,
            x: null,
            instagram: null,
            linkedin: null,
            createdAt: null,
            updatedAt: null,
            pais: null,
            ciudad: null
        }
    },
};

// Create Zustand store for authentication
export const useAuthStore = create(
    devtools((set, get) => ({
        // State
        auth: initialAuthState,
        errorMessage: '',
        successMessage: '',
        showLoading: false,
        isAuthReady: false,

        // Actions
        setLoading: (status) => {
            set({ showLoading: status });
        },

        setAuthReady: (status) => {
            set({ isAuthReady: status });
        },

        initializeAuth: () => {
            try {
                const data = JSON.parse(localStorage.getItem("userDetails"));

                if (data?.access_token && data?.user?.id) {
                    set({
                        auth: data,
                        isAuthReady: true,
                    });
                    console.log('[AUTH] Sesión recuperada del localStorage:', data.user.email, data.casUsername ? '(CAS)' : '(Tradicional)');
                } else {
                    console.log('[AUTH] No se encontró sesión en localStorage');
                    set({ isAuthReady: true });
                }
            } catch (error) {
                console.error('[AUTH] Error al recuperar sesión:', error);
                set({ isAuthReady: true });
            }
        },

        loginConfirmed: (data) => {
            set({
                auth: data,
                errorMessage: '',
                successMessage: 'Login Successfully Completed',
                showLoading: false,
                isAuthReady: true,
            });
        },

        loginFailed: (message) => {
            set({
                errorMessage: message,
                successMessage: '',
                showLoading: false,
                isAuthReady: true,
            });
        },

        signupConfirmed: (data) => {
            set({
                auth: data,
                errorMessage: '',
                successMessage: 'Signup Successfully Completed',
                showLoading: false,
            });
        },

        signupFailed: (message) => {
            set({
                errorMessage: message,
                successMessage: '',
                showLoading: false,
            });
        },

        logout: (history, shouldRedirect = true) => {
            // Obtener datos del usuario antes de limpiar
            const { auth } = get();
            const wasCASAuthenticated = auth.casUsername || auth.casAttributes;

            localStorage.removeItem('userDetails');

            // Lista de rutas públicas que no requieren redirección
            const publicRoutes = ['/', '/home', '/about-us', '/students-and-graduates', '/career-counseling', '/login', '/register', '/register-2', '/browse-job-list'];
            const currentPath = history?.location?.pathname || '/';

            set({
                auth: initialAuthState,
                errorMessage: '',
                successMessage: '',
            });

            // Si el usuario se autenticó con CAS, cerrar sesión también en CAS
            if (wasCASAuthenticated) {
                const CAS_LOGOUT_URL = process.env.REACT_APP_CAS_LOGOUT_URL || 'https://login.unicaribe.edu.do/cas/logout';
                const serviceUrl = `${window.location.origin}/home`;
                // Redirigir a CAS logout y luego volver a la aplicación
                window.location.href = `${CAS_LOGOUT_URL}?service=${encodeURIComponent(serviceUrl)}`;
                return; // No hacer más redirecciones porque CAS se encarga
            }

            // Solo redirigir si shouldRedirect es true y no estamos en una ruta pública
            if (shouldRedirect && !publicRoutes.includes(currentPath)) {
                if (history) {
                    history.push('/login');
                }
            }
        },

        // DEPRECATED: Usar useProfileStore en su lugar
        // Mantenidas temporalmente para compatibilidad con código legacy
        updateUserProfile: (profileData) => {
            const { auth } = get();
            set({
                auth: {
                    ...auth,
                    user: profileData,
                },
            });

            // También actualizar en localStorage
            const storageData = JSON.parse(localStorage.getItem('userDetails') || '{}');
            storageData.user = profileData;
            localStorage.setItem('userDetails', JSON.stringify(storageData));
        },

        // DEPRECATED: Usar useProfileStore.fetchProfile() en su lugar
        fetchUserProfile: async () => {
            const { updateUserProfile } = get();
            try {
                const response = await getUserProfile();
                updateUserProfile(response.data);
                return response.data;
            } catch (error) {
                console.error('Error al obtener el perfil del usuario:', error);
                throw error;
            }
        },

        login: async (email, password, history) => {
            const { loginConfirmed, loginFailed } = get();

            try {
                const response = await login(email, password);
                console.log(response)

                saveTokenInLocalStorage(response.data);

                // Calcular el timer basándose en expireDate (no expiresIn que no existe)
                const expireDate = new Date(response.data.expireDate);
                const now = new Date();
                const timer = expireDate.getTime() - now.getTime();

                console.log("⏰ Timer calculado para auto-logout:", timer, "ms (", Math.round(timer / 1000 / 60), "minutos)");

                if (timer > 0) {
                    runLogoutTimer(get, timer, history);
                } else {
                    console.warn("⚠️ El token ya expiró o expireDate inválido");
                }

                loginConfirmed(response.data);

                // Redirigir según el tipoPerfil
                const tipoPerfil = response.data.user?.tipoPerfil;
                if (tipoPerfil === 'USUARIO') {
                    history.push('/profile');
                } else if (tipoPerfil === 'EMPRESA') {
                    history.push('/company-profile');
                } else {
                    history.push('/home');
                }
            } catch (error) {
                const errorMessage = formatError(error.response?.data);
                loginFailed(errorMessage);
            }
        },

        signup: async (email, password, nombre, apellido, tipoPerfil, history, razonSocial = null) => {
            const { signupConfirmed, signupFailed } = get();

            try {
                const response = await signUp(email, password, nombre, apellido, tipoPerfil, razonSocial);

                saveTokenInLocalStorage(response.data);

                // Limpiar el tipo temporal del registro
                localStorage.removeItem('registerUserType');

                // Calcular el timer basándose en expireDate (no expiresIn que no existe)
                const expireDate = new Date(response.data.expireDate);
                const now = new Date();
                const timer = expireDate.getTime() - now.getTime();


                if (timer > 0) {
                    runLogoutTimer(get, timer, history);
                } else {
                    console.warn("⚠️ El token ya expiró o expireDate inválido");
                }

                signupConfirmed(response.data);

                // Redirigir según el tipoPerfil
                if (tipoPerfil === 'USUARIO') {
                    history.push('/profile');
                } else if (tipoPerfil === 'EMPRESA') {
                    history.push('/company-profile');
                } else {
                    history.push('/home');
                }
            } catch (error) {
                const errorMessage = formatError(error.response?.data || error);
                signupFailed(errorMessage);
            }
        },

        // Selector integrado - equivalente a isAuthenticated
        isAuthenticated: () => {
            const { auth } = get();
            return !!auth.access_token;
        },
    }))
);

// Export selector function for convenience
export const isAuthenticated = () => useAuthStore.getState().isAuthenticated();